// simpleCache.js
// Lightweight browser caching for API responses

const CACHE_PREFIX = 'rmkp_';
const CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Simple cache service using localStorage
 * Only caches API responses for 5 minutes to balance speed vs freshness
 */
class SimpleCache {
  constructor() {
    this.prefix = CACHE_PREFIX;
    this.expiry = CACHE_EXPIRY_MS;
  }

  /**
   * Get cached data if it exists and hasn't expired
   */
  get(key) {
    try {
      const cached = localStorage.getItem(this.prefix + key);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache has expired
      if (now - timestamp > this.expiry) {
        this.remove(key);
        return null;
      }

      return data;
    } catch (error) {
      // If there's any error reading cache, clear it
      this.remove(key);
      return null;
    }
  }

  /**
   * Set cached data with timestamp
   */
  set(key, data) {
    try {
      const cacheData = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(this.prefix + key, JSON.stringify(cacheData));
    } catch (error) {
      // If localStorage is full or unavailable, silently fail
      console.warn('Cache storage failed:', error);
    }
  }

  /**
   * Remove specific cache entry
   */
  remove(key) {
    try {
      localStorage.removeItem(this.prefix + key);
    } catch (error) {
      // Silently fail
    }
  }

  /**
   * Clear all cache entries
   */
  clear() {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      // Silently fail
    }
  }
}

export default new SimpleCache();
